import { useTranslation } from 'react-i18next';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import { formatInterestRate } from '@/localization/percentages';

const today = new Date().toLocaleDateString('sv-SE', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

export type LoanPartnerAgreementModalPayload = {
  email: string;
  nationalNumber: string;
  loanAmount: number;
  interestRate: number;
  setupFee: number;
  installmentFee: number;
  installmentAmount: number;
  numberOfInstallments: number;
  totalCost: number;
  totalRepaid: number;
  effectiveInterestRate: number;
};

type Props = ModalProps<LoanPartnerAgreementModalPayload>;

const LoanPartnerAgreement = ({
  payload: {
    email,
    nationalNumber,
    loanAmount,
    interestRate,
    setupFee,
    installmentFee,
    installmentAmount,
    numberOfInstallments,
    totalCost,
    totalRepaid,
    effectiveInterestRate,
  },
}: Props) => {
  const { t } = useTranslation('Common');

  return (
    <Modal title="Kreditavtal">
      <div className="flex flex-col gap-4">
        <p>{today}</p>
        <p>Avtalsnummer:</p>

        <h2 className="text-section-util">1. Allmän information</h2>

        <p>
          Nedan framgår de specifika egenskaperna och villkoren för Kredittagarens kredit. För övriga villkor
          och bestämmelser gäller Kreditgivarens Allmänna Villkor samt den betalningsplan som vid varje
          enskild tidpunkt gäller för krediten och som framgår av Kredittagarens Mina sidor på www.fairlo.se.
          Vid signering av detta Kreditavtal godkänner Kredittagaren i) att Kreditgivarens Allmänna Villkor är
          en del av detta kreditavtal och att Kredittagaren tagit del av och godkänt dessa, ii) att den
          betalningsplan som vid varje enskild tidpunkt godkänts av Kredittagaren med BankID via www.fairlo.se
          är en del av Kreditavtalet samt iii) att de betalningar som genomförs inom ramen för Krediten
          omfattas av Kreditavtalet.
        </p>

        <h2 className="text-section-util">2. Parter</h2>

        <div className="flex flex-col">
          <p className="text-body-semibold">Kredittagare</p>
          <span data-private>{email}</span>
          <span>
            Personnummer: <span data-private>{nationalNumber}</span>
          </span>
        </div>

        <div className="flex flex-col">
          <p className="text-body-semibold">Kreditgivare</p>
          <span>Fairlo AB</span>
          <span>Linnégatan 76</span>
          <span>115 23 Stockholm</span>
          <br />
          <span>Organisationsnummer: 559084-1101</span>
        </div>

        <h2 className="text-section-util">3. Krediten</h2>

        <dl className="[&>dt]:text-body-semibold [&>dd]:mb-2 [&>dt]:mb-1 [&>dt]:not-first:mt-4">
          <dt>Typ av kredit</dt>
          <dd>Kontokredit utan säkerhet för betalning av fakturor och andra betalningsförpliktelser.</dd>

          <dt>Nyttjande</dt>
          <dd>
            Krediten får användas för betalning av fakturor och andra betalningsförpliktelser som godkänns av
            Kreditgivaren inom ramen för beviljat Kreditutrymme. Återbetalningar medför inte att återbetalt
            Kreditutrymme automatiskt blir tillgängligt för nytt utnyttjande.
          </dd>

          <dt>Återbetalning</dt>
          <dd>Månadsbelopp i enlighet med en annuitetsplan.</dd>

          <dt>Kreditutrymme</dt>
          <dd>{t('money', { amount: loanAmount })}</dd>

          <dt>Ränta och avgifter</dt>
          <dd>
            Ränta utgår med en nominell årlig räntesats om {formatInterestRate(interestRate)} från den
            tidpunkt Krediten utbetalas.
            <br />
            <br />
            Vid utbetalningen av Krediten tillkommer en uppläggningsavgift: {t('money', { amount: setupFee })}
            <br />
            fakturaavgift (varje faktura): {t('money', { amount: installmentFee })}
          </dd>

          <dt>Månadsbelopp</dt>
          <dd>
            {t('money', { amount: installmentAmount })} per månad. Kredittagarens Månadsbelopp framgår av den
            Betalningsplan som gäller när Kreditavtalet ingås.
          </dd>

          <dt>Löptid</dt>
          <dd>Kreditens löptid är {numberOfInstallments} månader från dagen för Kreditavtalets ingående.</dd>

          <dt>Total Kostnad</dt>
          <dd>
            Den totala kostnaden för den vid Kreditavtalets ingående utnyttjade Krediten uppgår till{' '}
            {t('money', { amount: totalCost })} om Kredittagaren betalar i enlighet med Betalningsplanen som
            gäller när detta kreditavtal ingås.
          </dd>

          <dt>Effektiv ränta</dt>
          <dd>{formatInterestRate(effectiveInterestRate)}</dd>

          <dt>Totalt belopp</dt>
          <dd>
            Det sammanlagda belopp som Kredittagaren ska betala för den vid Kreditavtalets ingående utnyttjade
            Krediten uppgår till {t('money', { amount: totalRepaid })}, om Kredittagaren betalar i enlighet
            med Betalningsplanen som gäller när detta kreditavtal ingås.
          </dd>

          <dt>Dröjsmålsränta, övriga avgifter m.m.</dt>
          <dd>
            Vid försenad betalning har Kreditgivaren rätt att debitera en årlig dröjsmålsränta om 20 % över
            referensräntan på utestående belopp från förfallodagen till dess full betalning har kommit
            Kreditgivaren tillhanda. Kreditgivaren har vidare rätt att debitera påminnelseavgift om 60 kr per
            betalningspåminnelse.
            <br />
            <br />
            Om Kredittagaren återkallar sitt autogiromedgivande har Kreditgivaren rätt att debitera en avgift
            om 249 kronor per månad till dess att Krediten har återbetalats i sin helhet.
          </dd>

          <dt>Ändring av ränta och avgifter</dt>
          <dd>
            Kreditgivaren har rätt att ändra räntesats enligt de villkor som framgår av kreditavtalet.
            Kreditgivaren äger när som helst under kreditperioden rätt att besluta om höjning av särskild
            avgift i den mån Kreditgivarens kostnader ökat för den åtgärd avgiften avser att täcka.
            Kreditgivaren tillämpar ovanstående villkor om ändring av avgift även till Kredittagarens förmån
            och sänker således avgiften i den mån Kreditgivarens kostnader för den åtgärd avgiften avser att
            täcka minskat.
          </dd>
        </dl>

        <h2 className="text-section-util">4. Signering</h2>

        <p>
          Detta avtal har upprättats elektroniskt och signerats med personligt BankID av Kredittagaren.
          Kredittagaren har mottagit en kopia av detta kreditköpsavtal och kreditavtalet via e-post till den
          e-postadress som Kredittagaren angivit i sin ansökan om kredit.
        </p>
      </div>
    </Modal>
  );
};

export default LoanPartnerAgreement;
